import { BaseIconName } from '@cplug/design-system/components/props'
import { computed, type ComputedRef } from 'vue'
import { useSearchRoutes } from '@cplug/frontend-core/composables'
import type { SearchableRoute, SearchableRouteAction } from '@cplug/frontend-core/models'

// SearchableRouteAction no core v1.23.0 só tem subscription-hub | label-models.
const BANK_SLIPS_SEARCH_ACTION = 'bank-slips' as SearchableRouteAction

export const EXCLUDED_SEARCH_PATHS = new Set([
  '/dashboard/fisical-operations/terminal/ordering-modes/tables',
  '/dashboard/fisical-operations/terminal/ordering-modes/commands',
  '/dashboard/tax-settings/icms-group',
  '/dashboard/tax-settings/ipi-group',
  '/dashboard/tax-settings/pis-cofins-group',
  '/dashboard/settings/integrations',
  '/dashboard/plans/current-plan',
  '/dashboard/finance/bank-slips'
])

export const ERP_CUSTOM_SEARCH_ENTRIES: SearchableRoute[] = [
  {
    path: '/dashboard/plans/my-plan',
    title: 'Meu plano',
    description: 'Gerenciar plano contratado e faturas',
    category: 'Configurações',
    icon: BaseIconName.CreditCard,
    value: 'erp-my-plan',
    action: 'subscription-hub'
  },
  {
    path: '/dashboard/settings/label-models',
    title: 'Modelos de etiqueta',
    description: 'Gerenciar modelos de etiquetas para impressão',
    category: 'Configurações',
    icon: BaseIconName.Tags,
    value: 'erp-label-models',
    action: 'label-models'
  },
  {
    path: '/dashboard/finance/bank-slips',
    title: 'Boletos',
    description: 'Gerenciar boletos bancários e ativar Cora',
    category: 'Financeiro',
    icon: BaseIconName.Barcode,
    value: 'erp-bank-slips',
    action: BANK_SLIPS_SEARCH_ACTION
  },
  {
    path: '/dashboard/stock/stocks',
    title: 'Inventários',
    description: 'Gerenciar inventários de estoque',
    category: 'Estoque',
    icon: BaseIconName.Box,
    value: 'erp-stock-inventories'
  },
  {
    path: '/dashboard/stock/stocks',
    title: 'Balanço',
    description: 'Gerenciar balanço de estoque',
    category: 'Estoque',
    icon: BaseIconName.Box,
    value: 'erp-stock-balance'
  }
]

type NormalizedSearchableRoute = SearchableRoute & {
  _titleNorm?: string
  _valueNorm?: string
  _categoryNorm?: string
  _descriptionNorm?: string
  _keywordsNorm?: string
}

function normalizeText (text: string): string {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

function matchesQuery (route: NormalizedSearchableRoute, queryNorm: string): boolean {
  if (
    route._titleNorm?.includes(queryNorm) ||
    route._valueNorm?.includes(queryNorm) ||
    route._categoryNorm?.includes(queryNorm) ||
    route._descriptionNorm?.includes(queryNorm) ||
    route._keywordsNorm?.includes(queryNorm)
  ) {
    return true
  }

  return (
    normalizeText(route.title).includes(queryNorm) ||
    normalizeText(route.value).includes(queryNorm) ||
    normalizeText(route.category ?? '').includes(queryNorm) ||
    normalizeText(route.description ?? '').includes(queryNorm)
  )
}

export function useERPSearchRoutes (): {
  searchableRoutes: ComputedRef<SearchableRoute[]>
  searchRoutes: (query: string, limit?: number) => SearchableRoute[]
  } {
  const { searchableRoutes: baseRoutes } = useSearchRoutes()

  const searchableRoutes = computed<SearchableRoute[]>(() => {
    const filtered = (baseRoutes.value ?? []).filter(route => !EXCLUDED_SEARCH_PATHS.has(route.path))
    return [...filtered, ...ERP_CUSTOM_SEARCH_ENTRIES]
      .sort((a, b) => a.title.localeCompare(b.title))
  })

  const searchRoutes = (query: string, limit = 15): SearchableRoute[] => {
    if (!query.trim()) {
      return searchableRoutes.value.slice(0, 10)
    }

    const queryNorm = normalizeText(query.trim())

    return searchableRoutes.value
      .filter(route => matchesQuery(route as NormalizedSearchableRoute, queryNorm))
      .slice(0, limit)
  }

  return { searchableRoutes, searchRoutes }
}
